<template>
  <div class="audio-item-card-no-op-box">
    <AudioPlayer
      ref="refPlayer"
      :url="`${getObjectStorageFullUrl(props.item?.song_url)}`"
      :duration="renderData.duration"
      @syncDuration="syncDuration"
      @syncPlayState="syncPlayState"
      @syncLoadingState="syncLoadingState"
      @syncProgress="syncProgress"
    >
      <div
        class="audio-item-card-no-op"
        :class="`audio-item-card-no-op-box--${createRenderType} ${
          renderData.isCurPlay ? 'active' : ''
        }`"
      >
        <div class="aicno-label">{{ $$t("create.input_label") }}</div>
        <div class="aicno-description">
          {{ props.item?.prompt ?? "Make a song about my lover" }}
        </div>
        <div class="aicno-card">
          <LazyLoadImg :src="getObjectStorageFullUrl(props.item?.cover)" ></LazyLoadImg>
          <div class="aicno-card-time">
            {{
              mediaUtil.formatTime(
                renderData.duration -
                  (renderData.progress / 100) * renderData.duration
              )
            }}
          </div>
          <div class="aicno-card-playing flex-center" v-if="renderData.isplay">
            <LoaderWave :animation="true"></LoaderWave>
          </div>
        </div>
        <div class="aicno-play-control">
          <Button
            @click="doTogglePlay"
            :renderStyle="{
              '--padding': '0 15px',
              '--width': '108px',
              '--height': '48px',
              '--borderRadius': '50px',
              '--background':
                'linear-gradient(114deg, rgba(79, 222, 255, 1) 0%, rgba(230, 117, 255, 1) 100%)',
              '--fontColor': '#fff',
              '--fontSize': '14px',
              '--fontWeight': 'normal',
              '--border': 'none',
              '--backgroundHover':
                'linear-gradient(114deg, rgba(79, 222, 255, 0.8) 0%, rgba(230, 117, 255, 0.8) 100%)',
              '--fontColor': '#fff',
            }"
          >
            <i class="block play" v-show="!renderData.isplay"></i>
            <i class="block pause" v-show="renderData.isplay"></i>
          </Button>
        </div>
      </div>
    </AudioPlayer>
  </div>
</template>

<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  computed,
  readonly,
  reactive,
  watch,
  defineExpose,
  onMounted,
  onUpdated,
  onUnmounted,
  getCurrentInstance,
} from "vue";
import AudioWavForm from "@/components/media/audioPlayer/AudioWavForm.vue";
import LoaderWave from "@/components/animation/LoaderWave.vue";
import LazyLoadImg from "@/components/media/lazyLoad/LazyLoadImg.vue"

import Button from "@/components/basic/button/Button.vue";
import AudioPlayer from "@/components/media/audioPlayer/AudioPlayer.vue";
import { getObjectStorageFullUrl } from "@/lib/objectStorage/objectStorage.js";
import LoaderProgressCircle from "@/components/animation/LoaderProgressCircle.vue";
import mediaUtil from "@/components/media/utils/utils.js";
import { formatDateTime } from "@/utils/dateTime.js";
import { $$language, $$t } from "@/i18n/i18n.js";
import { storeToRefs } from "pinia";
import useCreateLayoutStore from "@/store/create/createLayoutStore.js";
const createLayoutStore = useCreateLayoutStore();
const { createRenderType } = storeToRefs(createLayoutStore);
import useUserDataStore from "@/store/userDataStore.js";
const userDataStore = useUserDataStore();
const globalProperties =
  (getCurrentInstance() || (window?.$getVueCurrentInstance?.())).appContext?.config?.globalProperties;

const appContext = (getCurrentInstance() || (window?.$getVueCurrentInstance?.())).appContext;

const formatData = (timestamp) => {
  return formatDate(timestamp, "YYYY-MM-DD hh:mm");
  // return formatDateTime(timestamp, $$language() == "cn");
};
const props = defineProps({
  item: {
    type: Object,
    default: null,
  },
});

const emits = defineEmits(["togglePlay"]);

const refPlayer = ref(null);
const refInput = ref(null);
const renderData = reactive({
  duration: (props.item?.duration_milliseconds ?? 0) / 1000,
  isplay: false,
  buffer: false,
  progress: 0,
  title: props.item?.title,
  isEditTitle: false,
  shareSuccessText: "",
  isMoreVisible: false,
  position: "bottom",
  isCurPlay: false,
});

const syncDuration = (duration) => {
  renderData.duration = duration;
};

const syncPlayState = (playState) => {
  renderData.isplay = playState;
  if (renderData.isplay) {
    globalProperties?.logClickEvent("create_basic_mode_showcase_play", {
      content_id: props.item?.song_id ?? "",
      type: userDataStore.isLogin ? "sign_in" : "not_sign_in",
    });
  }
};
const syncLoadingState = (loadingState) => {
  renderData.buffer = loadingState;
};
const syncProgress = (progress) => {
  renderData.progress = progress;
};

const doTogglePlay = () => {
  refPlayer.value.changeOp();
  emits("togglePlay", props.item);
};

const changeUrlListenerListener = () => {
  renderData.isCurPlay =
    appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.currentUrl ==
    getObjectStorageFullUrl(props.item?.mp3_url);
};
onMounted(() => {
  appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.registGlobalListener(
    "curPlayUrlChange",
    changeUrlListenerListener
  );
  changeUrlListenerListener();
  renderData.duration = (props.item?.duration_milliseconds ?? 0) / 1000;
});

onUnmounted(() => {
  appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.releaseGlobalListener(
    "curPlayUrlChange",
    changeUrlListenerListener
  );
});
</script>
<style lang="scss" scope>
.audio-item-card-no-op-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  .audio-item-card-no-op {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .aicno-label {
    font-family: HarmonyOS Sans SC;
    font-size: 18px;
    font-weight: bold;
    line-height: normal;

    background: linear-gradient(112deg, #4fdeff 0%, #e675ff 100%);
    color: transparent;
    background-clip: text;
  }
  .aicno-description {
    margin-top: 11px;
    font-family: HarmonyOS Sans SC;
    font-size: 16px;
    font-weight: normal;
    line-height: 24px;
    text-align: center;
    color: #ffffff;

    min-height: 48px;
    max-width: 220px;
  }
  .aicno-card {
    height: 180px;
    width: 180px;
    position: relative;
    margin: 16px 0;
    img {
      height: 100%;
      width: 100%;
      border-radius: 12px;
    }
    .aicno-card-time {
      position: absolute;
      left: 8px;
      top: 8px;
      height: 26px;
      line-height: 26px;
      border-radius: 8px;
      opacity: 1;
      padding: 0 8px;
      background: rgba(0, 0, 0, 0.2);
      backdrop-filter: blur(12px);

      font-family: HarmonyOS Sans SC;
      font-size: 12px;
      font-weight: 500;
      color: #ffffff;
    }
    .aicno-card-playing {
      position: absolute;
      right: 8px;
      bottom: 8px;
      width: 42px;
      height: 42px;
      border-radius: 42px;
      background: rgba(0, 0, 0, 0.2);
      backdrop-filter: blur(12px);
    }
  }

  .aicno-play-control {
    i {
      height: 24px;
      width: 24px;
    }
    i.play {
      background: url(@/assets/img/play-web-ico.png);
      background-size: 100% 100%;
    }
    i.pause {
      background: url(@/assets/img/pause-web-ico.png);
      background-size: 100% 100%;
    }
  }
}

@media screen and (max-width: 450px) {
}
</style>
